import { Code, ConnectError } from "@connectrpc/connect";
import type { BadgeVariant } from "@fox1ck-org/ui";

import {
  BindingStatus,
  TrackingDomainStatus,
  type TrackingDomain,
} from "@gen/tracker/v1/tracking_domain_pb";
import type { TKey } from "@/lib/i18n";

/**
 * What a media buyer is allowed to see instead of an enum.
 *
 * The wire carries two orthogonal fields — `status` (did someone switch this
 * domain off) and `bindingStatus` (does the address actually answer) — and the
 * screen showed both, in English, as `BINDING_STATUS_FAILED`. A buyer cannot
 * act on that. On production the one domain sat in `failed` from 7 September
 * and nothing on the screen said what had gone wrong or what to do.
 *
 * So: one derived state, five values, each with a sentence that names the next
 * step. Switched-off wins over the binding because it is the answer to "why is
 * my campaign not running" that the operator himself caused.
 *
 * Pure — the labels are i18n keys, not strings, so this module stays testable
 * and the copy stays in the dictionaries.
 */
export type DomainState = "working" | "waiting" | "failed" | "disabled" | "unknown";

export function domainState(d: {
  status: TrackingDomainStatus;
  bindingStatus: BindingStatus;
}): DomainState {
  if (d.status === TrackingDomainStatus.DISABLED) return "disabled";
  switch (d.bindingStatus) {
    case BindingStatus.BOUND:
      return "working";
    case BindingStatus.PENDING:
      return "waiting";
    case BindingStatus.FAILED:
      return "failed";
    default:
      return "unknown";
  }
}

export const STATE_TONE: Record<DomainState, BadgeVariant> = {
  working: "ok",
  waiting: "warn",
  failed: "danger",
  disabled: "neutral",
  unknown: "neutral",
};

export function stateLabelKey(s: DomainState): TKey {
  return `domains.state.${s}` as TKey;
}

export function stateHintKey(s: DomainState): TKey {
  return `domains.state.${s}.hint` as TKey;
}

/** Only a working domain lets `CampaignService.Activate` through. */
export function canRunCampaigns(d: {
  status: TrackingDomainStatus;
  bindingStatus: BindingStatus;
}): boolean {
  return domainState(d) === "working";
}

/**
 * The API answers `Unavailable` when no domain provider is configured. That is
 * not a failure the buyer can retry his way out of, so the screen has to stop
 * showing it as a red RPC banner and say "write to an administrator" instead.
 */
export function isProviderUnavailable(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.Unavailable;
}

/**
 * The short host most registrar forms ask for.
 *
 * The provider hands back a record whose `name` is either `@` or the full
 * fqdn; a registrar's "Name"/"Host" box usually wants only the label in front
 * of the zone. We cannot know where the buyer's zone is cut, so this returns
 * the first label and the UI offers it as a hint beside the full name rather
 * than replacing it. Pure.
 */
export function recordHost(fqdn: string, name: string): string {
  const full = !name || name === "@" ? fqdn : name;
  const first = full.split(".")[0];
  return first || full;
}

/** The record's `name` as it should be read: `@` means the domain itself. */
export function recordName(fqdn: string, name: string): string {
  return !name || name === "@" ? fqdn : name;
}

/**
 * The server's own words about a failed binding, or nothing.
 *
 * `bindingError` is free text written by the provider ("CNAME t.x.com → edge…
 * not found (saw …)"). It is the only thing that distinguishes a typo from an
 * outage, so it is shown verbatim — never swapped for a friendlier lie — under
 * a sentence that says what to do with it.
 */
export function bindingErrorText(d: Pick<TrackingDomain, "bindingError">): string {
  return d.bindingError?.trim() ?? "";
}
